<script setup>
import { ref, computed } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { usePolicy } from 'dashboard/composables/usePolicy';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Policy from 'dashboard/components/policy.vue';
import AgentSwitcher from 'dashboard/components-next/assistant/pageComponents/switcher/AgentSwitcher.vue';
import CreateAgentDialog from 'dashboard/components-next/assistant/pageComponents/agent/CreateAgentDialog.vue';

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1,
  },
  totalCount: {
    type: Number,
    default: 100,
  },
  itemsPerPage: {
    type: Number,
    default: 25,
  },
  headerTitle: {
    type: String,
    default: '',
  },
  // Описание страницы (по эталону «Команды»): рендерится под заголовком.
  description: {
    type: String,
    default: '',
  },
  // Плейсхолдер поиска: если задан — в строке действий появляется поле поиска
  // (v-model:search-query). По эталону «Команды».
  searchPlaceholder: {
    type: String,
    default: '',
  },
  // Простое центрированное сообщение пустого списка (как «В этом аккаунте нет
  // ни одной команды.»). Показывается при isEmpty, если не задан слот emptyState.
  emptyMessage: {
    type: String,
    default: '',
  },
  backUrl: {
    type: [String, Object],
    default: '',
  },
  buttonPolicy: {
    type: Array,
    default: () => [],
  },
  buttonLabel: {
    type: String,
    default: '',
  },
  featureFlag: {
    type: String,
    default: '',
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  isEmpty: {
    type: Boolean,
    default: false,
  },
  showPaginationFooter: {
    type: Boolean,
    default: true,
  },
  showAgentSwitcher: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click', 'close', 'update:currentPage']);

// Строка поиска (двусторонняя привязка) — по эталону BaseSettingsHeader.
const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const { t } = useI18n();

const route = useRoute();
const { shouldShowPaywall } = usePolicy();

const showAgentSwitcherDropdown = ref(false);
const createAgentDialogRef = ref(null);

const agents = useMapGetter('assistantAgents/getRecords');
const uiFlags = useMapGetter('assistantAgents/getUIFlags');

const currentAgentId = computed(() => route.params.agentId);
const isFetchingAgents = computed(() => uiFlags.value?.fetchingList);

const activeAgentName = computed(() => {
  return (
    agents.value?.find(agent => agent.id === Number(currentAgentId.value))
      ?.name || t('ASSISTANT.AGENT_SWITCHER.NEW_AGENT')
  );
});

const showPaywall = computed(() => {
  return shouldShowPaywall(props.featureFlag);
});

const handleButtonClick = () => {
  emit('click');
};

const handlePageChange = event => {
  emit('update:currentPage', event);
};

const toggleAgentSwitcher = () => {
  showAgentSwitcherDropdown.value = !showAgentSwitcherDropdown.value;
};

const handleCreateAgent = () => {
  showAgentSwitcherDropdown.value = false;
  createAgentDialogRef.value.dialogRef.open();
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="px-6">
      <div class="w-full max-w-5xl mx-auto pt-6">
        <!-- Строка заголовка (эталон «Команды»): переключатель агента + заголовок,
             без кнопки создания — она в строке поиска ниже. -->
        <div class="flex flex-wrap items-center gap-3 min-h-8 mb-2">
          <BackButton v-if="backUrl" :back-url="backUrl" />
          <div
            v-if="showAgentSwitcher && !showPaywall"
            class="flex items-center gap-2"
          >
            <span
              v-if="!isFetchingAgents"
              class="text-xl font-medium truncate text-n-slate-12"
            >
              {{ activeAgentName }}
            </span>
            <div class="relative group">
              <OnClickOutside @trigger="showAgentSwitcherDropdown = false">
                <Button
                  icon="i-lucide-chevron-down"
                  :variant="showAgentSwitcherDropdown ? 'faded' : 'ghost'"
                  color="slate"
                  size="xs"
                  :disabled="isFetchingAgents"
                  :is-loading="isFetchingAgents"
                  class="rounded-md group-hover:bg-n-slate-3 hover:bg-n-slate-3 [&>span]:size-4"
                  @click="toggleAgentSwitcher"
                />

                <AgentSwitcher
                  v-if="showAgentSwitcherDropdown"
                  class="absolute ltr:left-0 rtl:right-0 top-9"
                  @close="showAgentSwitcherDropdown = false"
                  @create-agent="handleCreateAgent"
                />
              </OnClickOutside>
            </div>
          </div>
          <div
            v-if="showAgentSwitcher && !showPaywall && headerTitle"
            class="w-0.5 h-4 rounded-2xl bg-n-weak"
          />
          <h1 v-if="headerTitle" class="text-heading-1 text-n-slate-12">
            {{ headerTitle }}
          </h1>
        </div>

        <!-- Описание -->
        <div
          v-if="description || $slots.subHeader"
          class="flex flex-col w-full gap-1.5 text-n-slate-11"
        >
          <p
            v-if="description"
            class="mb-0 line-clamp-5 sm:line-clamp-none max-w-3xl text-body-main"
          >
            {{ description }}
          </p>
          <slot name="subHeader" />
        </div>

        <!-- Строка «поиск слева / действия справа» (эталон «Команды») -->
        <div
          v-if="
            !showPaywall &&
            (searchPlaceholder || buttonLabel || $slots.headerActions)
          "
          class="flex justify-between gap-3 mt-4 mb-4 min-w-0"
        >
          <div class="flex items-center gap-3 min-w-0">
            <Input
              v-if="searchPlaceholder"
              v-model="searchQuery"
              :placeholder="searchPlaceholder"
              size="sm"
              type="search"
              class="group w-56 min-w-0 hidden sm:flex [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:!rounded-[0.625rem]"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <div
              v-if="buttonLabel"
              v-on-clickaway="() => emit('close')"
              class="relative group/assistant-button"
            >
              <Policy :permissions="buttonPolicy">
                <Button
                  :label="buttonLabel"
                  size="sm"
                  class="group-hover/assistant-button:brightness-110"
                  @click="handleButtonClick"
                />
              </Policy>
              <slot name="action" />
            </div>
            <slot name="headerActions" />
          </div>
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl h-full mx-auto pb-4">
        <slot v-if="!showPaywall" name="controls" />
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div v-else-if="showPaywall">
          <slot name="paywall" />
        </div>
        <template v-else-if="isEmpty">
          <p
            v-if="emptyMessage"
            class="flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
          >
            {{ emptyMessage }}
          </p>
          <slot v-else name="emptyState" />
        </template>
        <slot v-else name="body" />
        <slot />
      </div>
    </main>
    <footer v-if="showPaginationFooter" class="sticky bottom-0 z-10">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="itemsPerPage"
        class="max-w-[67rem]"
        @update:current-page="handlePageChange"
      />
    </footer>
    <CreateAgentDialog ref="createAgentDialogRef" type="create" />
  </section>
</template>
